<script lang="ts" setup>
import { computed } from 'vue'

import { useStore } from '@/store'
import { actionLabels, AutomationGroup, AutomationTypeEnum, ConditionOperatorEnum, GroupSearchOpEnum } from '@/store/modules/automation/types'
import { ModeEnum } from '@/store/modules/settings/types'

import CriteriaDisplayer from '../filters/CriteriaDisplayer.vue'

const store = useStore()

export interface AutomationGroupDetailsProps {
  fullAutomationGroup: AutomationGroup
}

const props = defineProps<AutomationGroupDetailsProps>()

const enableWebpimsAccess = computed(() => store.state.settings.connectorSettings.connectorTypes.includes('webpims'))
const internalOperator = computed(() => props.fullAutomationGroup.searchOperators === GroupSearchOpEnum.ORAND
  ? ConditionOperatorEnum.AND
  : ConditionOperatorEnum.OR
)
const externalOperator = computed(() => props.fullAutomationGroup.searchOperators === GroupSearchOpEnum.ANDOR
  ? ConditionOperatorEnum.AND
  : ConditionOperatorEnum.OR
)
const getAutomationGroupRequest = computed(() => store.state.automation.getAutomationGroupRequest)
</script>

<template>
  <div class="m-2">
    <div
      v-if="getAutomationGroupRequest.fetching"
      class="loader loader-center"
    />
    <template v-else>
      <div
        v-if="fullAutomationGroup.presentGhmList.length"
      >
        GHM détecté(s) :
        <div class="badge text-bg-primary">
          {{ fullAutomationGroup.presentGhmList.join(', ') }}
        </div>
      </div>
      <div
        v-if="fullAutomationGroup.absentGhmList.length"
      >
        GHM exclu(s) :
        <div class="badge text-bg-primary">
          {{ fullAutomationGroup.absentGhmList.join(', ') }}
        </div>
      </div>
      <div
        v-for="action in fullAutomationGroup.actions"
        :key="action.id"
      >
        {{ actionLabels[action.action] }} :
        <div class="badge text-bg-primary">
          {{ action.value }}
        </div>
      </div>
      <hr>
      <div
        v-if="fullAutomationGroup.globalCriteriaGroup !== null"
      >
        <criteria-displayer
          class="card mb-3"
          :can-remove-criteria="false"
          :display-grouped-criteria="true"
          :internal-operator="internalOperator"
          :criteria-group="fullAutomationGroup.globalCriteriaGroup"
          :mode="ModeEnum.AUTOMATION"
        />
      </div>
      <div
        v-for="(criteriaGroup, idx) in fullAutomationGroup.criteriaGroups"
        :key="idx"
      >
        <div v-if="idx > 0">
          <div class="text-center">
            {{ externalOperator }}
          </div>
        </div>
        <criteria-displayer
          class="card mb-3"
          :can-remove-criteria="false"
          :internal-operator="internalOperator"
          :criteria-group="criteriaGroup"
          :mode="ModeEnum.AUTOMATION"
        />
      </div>
      <div
        v-if="fullAutomationGroup.criteriaGroups.length === 0"
        class="text-center"
      >
        <small>Ce groupe ne contient aucun critère supplémentaire.</small>
      </div>
      <hr>
      <div v-if="fullAutomationGroup.type === AutomationTypeEnum.AI">
        Quand une biopsie est demandée, attendre l'acte ou le compte-rendu correspondant :
        <div class="badge text-bg-primary">
          {{ fullAutomationGroup.fulfillAnapathRequests === true ? 'OUI' : 'NON' }}
        </div>
      </div>
      <div v-if="fullAutomationGroup.type === AutomationTypeEnum.AI">
        Attendre un acte d'anesthésie pour les GHM de type K :
        <div class="badge text-bg-primary">
          {{ fullAutomationGroup.waitForAnesthesia === true ? 'OUI' : 'NON' }}
        </div>
      </div>
      <div v-if="enableWebpimsAccess && fullAutomationGroup.type === AutomationTypeEnum.AI">
        Alerter si pas de CRO (GHM type C et K) :
        <div class="badge text-bg-primary">
          {{ fullAutomationGroup.alertNoCro === true ? 'OUI' : 'NON' }}
        </div>
      </div>
      <div v-if="enableWebpimsAccess && fullAutomationGroup.type === AutomationTypeEnum.AI">
        Alerter si pas de CRH :
        <div class="badge text-bg-primary">
          {{ fullAutomationGroup.alertNoCrh === true ? 'OUI' : 'NON' }}
        </div>
      </div>
      <div>
        Délai d'attente :
        <div class="badge text-bg-primary">
          {{ fullAutomationGroup.waitDuration }} j
        </div>
      </div>
    </template>
  </div>
</template>
